import React, { useEffect, useRef, useState } from "react";
import { FaceMesh } from "@mediapipe/face_mesh";
import { Camera } from "@mediapipe/camera_utils";
import "./style.css";

function Megane() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const previewImageRef = useRef(null);
  const previewModalRef = useRef(null);
  const [currentFacing, setCurrentFacing] = useState("user");
  const glassesImgRef = useRef(new Image());
  const faceMeshRef = useRef(null);
  const cameraRef = useRef(null);

  useEffect(() => {
    const img = glassesImgRef.current;
    img.src = "/IMG_8028.PNG";
    img.onload = () => {
      console.log("🖼️ メガネ画像読み込み完了");
    };
  }, []);

  const drawGlasses = (landmarks, canvas, ctx) => {
    const img = glassesImgRef.current;
    if (!img.complete) return;

    const leftEye = landmarks[133];
    const rightEye = landmarks[362];

    // キャンバスのトリミング範囲を考慮
    const video = videoRef.current;
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    const targetWidth = canvas.width;
    const targetHeight = canvas.height;

    // 縦長レイアウトでのトリミング計算
    const videoAspectRatio = videoWidth / videoHeight;
    const targetAspectRatio = targetWidth / targetHeight;

    let sx, sy, sw, sh;
    if (videoAspectRatio > targetAspectRatio) {
      // ビデオが横長の場合、左右を切り取る
      sh = videoHeight;
      sw = videoHeight * targetAspectRatio;
      sx = (videoWidth - sw) / 2;
      sy = 0;
    } else {
      // ビデオが縦長の場合、上下を切り取る
      sw = videoWidth;
      sh = videoWidth / targetAspectRatio;
      sx = 0;
      sy = (videoHeight - sh) / 2;
    }

    // ランドマーク座標をキャンバス座標に変換
    const leftEyeX = ((leftEye.x * videoWidth - sx) / sw) * targetWidth;
    const leftEyeY = ((leftEye.y * videoHeight - sy) / sh) * targetHeight;
    const rightEyeX = ((rightEye.x * videoWidth - sx) / sw) * targetWidth;
    const rightEyeY = ((rightEye.y * videoHeight - sy) / sh) * targetHeight;

    // メガネの描画位置を計算
    const dx = rightEyeX - leftEyeX;
    const dy = rightEyeY - leftEyeY;
    const angle = Math.atan2(dy, dx);
    const eyeDistance = Math.sqrt(dx * dx + dy * dy);
    const glassesWidth = eyeDistance * 4.0;
    const glassesHeight = glassesWidth * (img.height / img.width);

    const centerX = (leftEyeX + rightEyeX) / 2;
    const centerY = (leftEyeY + rightEyeY) / 2;

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate(angle);
    ctx.drawImage(
      img,
      -glassesWidth / 2,
      -glassesHeight / 2,
      glassesWidth,
      glassesHeight
    );
    ctx.restore();
  };

  const handleCapture = () => {
    const canvas = canvasRef.current;
    const photoUrl = canvas.toDataURL("image/jpeg", 0.95);
    previewImageRef.current.src = photoUrl;
    previewModalRef.current.style.display = "block";
  };

  const handleRetake = () => {
    previewModalRef.current.style.display = "none";
  };

  const handleBack = () => {
    window.location.href = "/";
    // previewModalRef.current.style.display = "none";
  };

  const handleSwitchCamera = async () => {
    if (cameraRef.current) {
      await cameraRef.current.stop();
      cameraRef.current = null;
    }
    if (faceMeshRef.current) {
      faceMeshRef.current.close();
      faceMeshRef.current = null;
    }
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach((t) => t.stop());
      videoRef.current.srcObject = null;
    }
    setCurrentFacing((prev) => (prev === "user" ? "environment" : "user"));
  };

  useEffect(() => {
    let isActive = true;

    const setup = async () => {
      const faceMesh = new FaceMesh({
        locateFile: (file) =>
          `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
      });
      await faceMesh.setOptions({
        maxNumFaces: 8,
        minDetectionConfidence: 0.7,
        minTrackingConfidence: 0.7,
      });

      faceMesh.onResults((results) => {
        if (!isActive) return;

        const video = videoRef.current;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");

        const vw = video.videoWidth;
        const vh = video.videoHeight;

        // アスペクト比を9:16に設定（縦長）
        const targetAspectRatio = 9 / 16;

        // 画面サイズに基づいて描画サイズを計算
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;

        // 画面の95%の幅を使用し、高さはアスペクト比に基づいて計算
        let targetWidth = Math.min(vw, Math.floor(screenWidth * 0.95));
        let targetHeight = Math.floor(targetWidth / targetAspectRatio);

        // 高さが画面をはみ出す場合は高さベースで調整
        if (targetHeight > screenHeight * 0.95) {
          targetHeight = Math.floor(screenHeight * 0.95);
          targetWidth = Math.floor(targetHeight * targetAspectRatio);
        }

        canvas.width = targetWidth;
        canvas.height = targetHeight;

        // ビデオのトリミング計算
        const videoAspectRatio = vw / vh;
        let sx, sy, sw, sh;

        if (videoAspectRatio > targetAspectRatio) {
          // ビデオが横長の場合、左右を切り取る
          sh = vh;
          sw = vh * targetAspectRatio;
          sx = (vw - sw) / 2;
          sy = 0;
        } else {
          // ビデオが縦長の場合、上下を切り取る
          sw = vw;
          sh = vw / targetAspectRatio;
          sx = 0;
          sy = (vh - sh) / 2;
        }

        ctx.save();
        if (currentFacing === "user") {
          ctx.translate(canvas.width, 0);
          ctx.scale(-1, 1);
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(
          results.image,
          sx,
          sy,
          sw,
          sh,
          0,
          0,
          targetWidth,
          targetHeight
        );

        if (results.multiFaceLandmarks) {
          results.multiFaceLandmarks.forEach((landmarks) => {
            drawGlasses(landmarks, canvas, ctx);
          });
        }
        ctx.restore();
      });

      faceMeshRef.current = faceMesh;

      const camera = new Camera(videoRef.current, {
        onFrame: async () => {
          if (!isActive || !faceMeshRef.current) return;
          await faceMeshRef.current.send({ image: videoRef.current });
        },
        width: 1920,
        height: 1080,
        facingMode: currentFacing,
      });

      await camera.start();
      cameraRef.current = camera;
    };

    setup();

    return () => {
      isActive = false;
      if (cameraRef.current) {
        cameraRef.current.stop();
        cameraRef.current = null;
      }
      if (faceMeshRef.current) {
        faceMeshRef.current.close();
        faceMeshRef.current = null;
      }
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject.getTracks().forEach((t) => t.stop());
        videoRef.current.srcObject = null;
      }
    };
  }, [currentFacing]);

  return (
    <div
      className="home-container"
      style={{
        height: "100dvh",
        width: "100vw",
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "black",
      }}
    >
      <video
        id="video"
        ref={videoRef}
        autoPlay
        playsInline
        muted
        crossOrigin="anonymous"
        style={{ display: "none" }} // ビデオを非表示に戻す
      />
      <canvas
        ref={canvasRef}
        className="preview-canvas"
        style={{
          maxWidth: "100vw",
          maxHeight: "100dvh",
          objectFit: "contain",
        }}
      />
      <button className="switch-camera-btn" onClick={handleSwitchCamera}>
        <img src="/change.png" alt="カメラ切り替え" />
      </button>
      <button className="capture-btn" onClick={handleCapture}>
        <img src="/camera_icon.svg" alt="撮影" />
      </button>
      <div
        ref={previewModalRef}
        className="preview-modal"
        style={{ display: "none" }}
      >
        <div className="modal-content">
          <img
            ref={previewImageRef}
            alt="プレビュー"
            className="preview-image"
          />
          <div className="save-instruction">画像を長押しで保存できます</div>
          <div className="button-container">
            <button onClick={handleRetake} className="retake-btn">
              再撮影
            </button>
            <button onClick={handleBack} className="back-btn">
              トップに戻る
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Megane;
